import { Avatar, type AvatarProps, type MantineRadius } from '@mantine/core';

export type EntityLogoProps = {
  name: string;
  /** @desc Tile edge in px. Figma draws listing tiles at 40. */
  size?: number;
  /** @desc Corner radius; `md` (8px) for the 40px tile, `lg` (16px) for the 96px masthead tile (C3). */
  radius?: MantineRadius;
  /** @desc A brand logo's URL, already resolved (a CMS image, a shipped asset). */
  src?: string | null;
  /** @desc A flag image's URL, already resolved; used when there is no logo. */
  flagSrc?: string | null;
  /** @desc Override the letter(s) shown, e.g. a market code (`GB`) instead of the initial. */
  code?: string;
};

/**
 * @desc A flag is drawn edge to edge in the tile. A hairline inset keeps a mostly-white flag
 * (Japan, Poland) from dissolving into a white card; it sits inside the image, so the tile's size
 * is the same as a logo's or an initial's. `--charlie-hairline` is scheme-aware (theme resolver).
 */
const FLAG_TILE: AvatarProps['styles'] = {
  root: { boxShadow: 'inset 0 0 0 1px var(--charlie-hairline)' },
};

/**
 * @desc A logo is fitted, never cropped. Mantine's Avatar image is `object-fit: cover`, which cut
 * a wide wordmark (DraftKings, Paddy Power) down to the middle of a square. `contain` shows the
 * whole mark at its own proportions, centred, whether it is wide, tall or square; the padding is a
 * percentage of the tile's edge (a percentage pads against the containing block's width), so the
 * quiet margin scales from the 18px chip to the 96px masthead without a size-specific value.
 */
const LOGO_TILE: AvatarProps['styles'] = {
  image: { objectFit: 'contain', padding: '12%' },
};

const TILE_STYLES = { logo: LOGO_TILE, flag: FLAG_TILE, initial: undefined } as const;

/**
 * @desc A company's or market's tile (decision D-8): a brand logo or a flag when one exists,
 * otherwise the initial letter(s) on a quiet navy tile — consistent with the initials-only avatar
 * rule (no photos). Logos sit inside the same rounded square the flags fill. Decorative next to
 * the name it accompanies, so it is hidden from assistive tech.
 *
 * Props only: the caller resolves asset URLs (the portal's wrapper maps a flag code or brand slug
 * to its build asset), so a fragment can render the same tile from a CMS image URL.
 */
export function EntityLogo({
  name,
  size = 40,
  radius = 'md',
  src,
  flagSrc,
  code,
}: EntityLogoProps) {
  const kind = src ? 'logo' : flagSrc ? 'flag' : 'initial';
  const letters = (code ?? name.trim().charAt(0)).toUpperCase();
  return (
    <Avatar
      src={src || flagSrc || undefined}
      alt=""
      aria-hidden
      size={size}
      radius={radius}
      variant="light"
      color="navy"
      fw={700}
      styles={TILE_STYLES[kind]}
      data-testid="entity-logo"
      data-kind={kind}
    >
      {letters}
    </Avatar>
  );
}
